import { useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { AgeCategory } from '@mews-commander/data-access';
import { FormCounterField, FormCounterFieldProps, LocalizedProperty } from '@mews/framework';
import { Stack, StackItem, StackItemProps, StackProps } from '@optimus-web/core';

export const ageCategoriesCounterFieldsSchema = z.record(z.number());

type AgeCategoriesCounterFieldsValues = z.infer<typeof ageCategoriesCounterFieldsSchema>;

interface AgeCategoriesCounterFieldProps extends Omit<FormCounterFieldProps, 'name' | 'onChange'> {
    ageCategories: AgeCategory[];
    maxCapacity?: number;
    name: string;
    counterWrapperProps?: Partial<StackItemProps>;
    wrapperProps?: Partial<StackProps>;
}

export const AgeCategoriesCounterField: React.FC<AgeCategoriesCounterFieldProps> = ({
    ageCategories,
    maxCapacity,
    name,
    wrapperProps = {},
    counterWrapperProps = {},
    ...other
}) => {
    const { watch } = useFormContext<{ [K in typeof name]: AgeCategoriesCounterFieldsValues }>();

    const ageCategoryCounterValues = watch(name);

    const personSum = Object.values(ageCategoryCounterValues).reduce((acc, item) => item + acc, 0);

    return (
        <Stack spacing="100" inline wrap distribution="space-between" {...wrapperProps}>
            {ageCategories.map((category) => (
                <StackItem key={category.id} {...counterWrapperProps}>
                    <FormCounterField
                        data-test-id={`age-counter-${category.id}`}
                        name={`${name}.${category.id}`}
                        size="medium"
                        label={<LocalizedProperty property={category.name} />}
                        min={0}
                        disabled={!!maxCapacity && !ageCategoryCounterValues[category.id] && personSum === maxCapacity}
                        max={
                            maxCapacity
                                ? (ageCategoryCounterValues[category.id] ?? 0) + maxCapacity - personSum
                                : undefined
                        }
                        data-age-category-id={category.id}
                        {...other}
                    />
                </StackItem>
            ))}
        </Stack>
    );
};
